import { useState, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { STORAGE_KEYS } from '@/utils/constants';
import type { SavedAction, UserRole } from '@/types';
import { DEFAULT_SAVED_ACTIONS_BY_ROLE } from '@/config/static/workspace';

function sortActions(actions: SavedAction[]): SavedAction[] {
  return [...actions].sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));
}

export function useSavedActions(role: UserRole) {
  const defaultActions = DEFAULT_SAVED_ACTIONS_BY_ROLE[role] ?? [];
  const [storedActions, setStoredActions] = useLocalStorage<SavedAction[]>(
    STORAGE_KEYS.SAVED_ACTIONS,
    defaultActions
  );

  const [actions, setActions] = useState<SavedAction[]>(() =>
    sortActions(storedActions.length === 0 ? defaultActions : storedActions)
  );

  const saveAction = useCallback(
    (action: SavedAction) => {
      setActions((prev) => {
        const updated = sortActions(prev.map((a) => (a.id === action.id ? action : a)));
        setStoredActions(updated);
        return updated;
      });
    },
    [setStoredActions]
  );

  const removeAction = useCallback(
    (id: string) => {
      setActions((prev) => {
        const updated = prev.filter((a) => a.id !== id);
        setStoredActions(updated);
        return updated;
      });
    },
    [setStoredActions]
  );

  const togglePin = useCallback(
    (id: string) => {
      setActions((prev) => {
        const updated = sortActions(
          prev.map((a) => (a.id === id ? { ...a, isPinned: !a.isPinned } : a))
        );
        setStoredActions(updated);
        return updated;
      });
    },
    [setStoredActions]
  );

  const isTemplatePinned = useCallback(
    (template: string) => {
      const trimmed = template.trim();
      return actions.some((a) => a.template === trimmed && a.isPinned);
    },
    [actions]
  );

  const pinTemplate = useCallback(
    (template: string) => {
      const trimmed = template.trim();
      if (!trimmed) return;

      setActions((prev) => {
        const existing = prev.find((a) => a.template === trimmed);
        const updated = existing
          ? sortActions(
              prev.map((a) =>
                a.id === existing.id ? { ...a, isPinned: !a.isPinned } : a
              )
            )
          : sortActions([
              { id: `saved-${Date.now()}`, template: trimmed, isPinned: true },
              ...prev,
            ]);
        setStoredActions(updated);
        return updated;
      });
    },
    [setStoredActions]
  );

  return { actions, saveAction, removeAction, togglePin, isTemplatePinned, pinTemplate };
}
